import { AutocompleteSelect, Button, ButtonVariants, Checkbox, FormSection, Text, Textarea, TextInput } from "@centricsoftware/design-system";
import { REMOVE_RES } from "csi/components/constants";
import { RuleStoreContext } from "csi/middleware/attributeRule/RuleStore";
import CustomViewFilterPane from "csi/view/customview/CustomViewFilterPane";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import AggregateStoreGroupStore from "./AggregateStoreGroupStore";
import DataStoreDialog from "./DataStoreDialog";

interface AggregateStoreGroupProps {
	group: AggregateStoreGroupStore;
}

interface AggregateStoreDataStoreInfoProps {
	group: AggregateStoreGroupStore;
}

const AggregateStoreDataStoreInfo: FunctionComponent<AggregateStoreDataStoreInfoProps> = observer(function AggregateStoreDataStoreInfo(props) {
	const {
		alternative: {
			aggregateInfoStore: {
				loc,
				overridden,
			},
		},
		formattedQuery,
		groupInfo: {
			dataStoreInfo: {
				limitPaths,
				pathHintsId,
			},
		},
		hasDataStoreInfo,
		ruleStore,
	} = props.group;

	// TODO: show sort conditions separate from query?

	return (
		<>
			{!!limitPaths?.length && (
				<Textarea
					labelRes={{ res: "DataStoreDefinition.Attr.LimitPaths" }}
					minRows={1}
					readOnly
					value={limitPaths.join("\n")}
				/>
			)}
			{!!pathHintsId && (
				<TextInput
					labelRes={{ res: "DataStoreDefinition.Attr.PathHints" }}
					readOnly
					value={pathHintsId}
				/>
			)}
			{!!formattedQuery && (
				<Textarea
					labelRes={{ res: "Query" }}
					readOnly
					value={formattedQuery}
				/>
			)}
			{!hasDataStoreInfo && (
				<Text align="start">
					{loc.resource("AggregateStoreNoDataStoreInfo")}
				</Text>
			)}
			{!overridden && (
				<RuleStoreContext.Provider value={ruleStore}>
					<FormSection title="Additional Query Filters" />
					<CustomViewFilterPane />
				</RuleStoreContext.Provider>
			)}
		</>
	);
});

const AggregateStoreGroupLabel: FunctionComponent<AggregateStoreGroupProps> = observer(function AggregateStoreGroupLabel(props) {
	const { group } = props;
	const {
		aggregateInfoStore: {
			readOnly,
		},
		label,
		labelDisabled,
		labelError,
		labelPlaceholder,
		labelRequired,
		labelResource,
		labelResourceRequired,
		labelResourceError,
		onChangeLabel,
		onChangeLabelResource,
	} = group;

	const showLabel = !readOnly || !!label || !!labelPlaceholder;
	const showLabelResource = !readOnly || !!labelResource;

	if (showLabel) {
		return (
			<>
				{showLabelResource && (
					<TextInput
						error={!!labelResourceError}
						errorMessage={labelResourceError}
						labelRes={{ res: "AggregateStoreGroup.Attr.Node Name" }}
						onChange={onChangeLabelResource}
						readOnly={readOnly}
						required={labelResourceRequired}
						value={labelResource}
					/>
				)}
				<TextInput
					disabled={labelDisabled}
					error={!!labelError}
					errorMessage={labelError}
					labelRes={{ res: "Label" }}
					onBlurChange={onChangeLabel}
					placeholder={labelPlaceholder}
					readOnly={readOnly}
					required={labelRequired}
					value={label}
				/>
			</>
		);
	}

	return null;
});

const AggregateStoreGroup: FunctionComponent<AggregateStoreGroupProps> = observer(function AggregateStoreGroup(props) {
	const { group } = props;
	const {
		aggregateInfoStore: {
			overridden,
			readOnly,
			sourceClass,
		},
		alternative: {
			dynamic,
		},
		canRemoveGroup,
		dataStoreDialogStore,
		dataStoreIdDisabled,
		dataStoreIdError,
		dataStoreIdLinkButtonProps,
		dataStoreOptions,
		dataStoreOptionValue,
		groupInfo: {
			dataStoreId,
			suggested,
		},
		hasValidDataStoreId,
		onChangeDataStore,
		onChangeDataStoreId,
		onChangePath,
		onChangeSuggested,
		onChangeTargetClass,
		onClickRemove,
		onOpenDataStore,
		path,
		pathError,
		suggestedDataStoreGroups,
		targetClass,
		targetClassOptions,
	} = group;

	return (
		<>
			<FormSection title="Aggregate Store Group" />
			{canRemoveGroup && (
				<Button
					childrenRes={REMOVE_RES}
					onClick={onClickRemove}
					variant={ButtonVariants.secondary}
				/>
			)}
			{!dynamic && (
				<>
					{(!readOnly || suggested) && (
						<Checkbox
							ariaLabelRes={{ res: "Suggested" }}
							checked={suggested}
							labelRes={{ res: "Suggested" }}
							onChange={onChangeSuggested}
							readOnly={readOnly}
						/>
					)}
					<AggregateStoreGroupLabel group={group} />
					{(!readOnly || !!path) && (
						<TextInput
							error={!!pathError}
							errorMessageRes={{ res: pathError, params: [sourceClass] }}
							labelRes={{ res: "AggregateStoreGroup.Attr.Path" }}
							onBlurChange={onChangePath}
							readOnly={readOnly}
							value={path || ""}
						/>
					)}
					{targetClassOptions && (
						<AutocompleteSelect
							disableClearable
							labelRes={{ res: "TargetClass" }}
							onChange={onChangeTargetClass}
							options={targetClassOptions}
							required
							value={targetClass}
						/>
					)}
				</>
			)}
			{overridden ? (
				<AutocompleteSelect
					disableClearable
					disabled={dataStoreIdDisabled}
					dropdownButtonAriaLabelRes={{ res: "AggregateStoreGroup.Attr.DataStoreId" }}
					error={!!dataStoreIdError}
					errorMessage={dataStoreIdError}
					grouped
					labelRes={{ res: "AggregateStoreGroup.Attr.DataStoreId" }}
					linkButtonProps={dataStoreIdLinkButtonProps}
					onChange={onChangeDataStore}
					onOpen={onOpenDataStore}
					options={dataStoreOptions}
					required
					suggestedGroups={suggestedDataStoreGroups}
					suggestedLabelRes={{ res: "SelectedUpperCase" }}
					value={dataStoreOptionValue}
				/>
			) : (
				<TextInput
					disabled={!!pathError}
					error={!!dataStoreIdError}
					errorMessage={dataStoreIdError}
					labelRes={{ res: "AggregateStoreGroup.Attr.DataStoreId" }}
					onBlurChange={onChangeDataStoreId}
					readOnly={readOnly}
					required={!readOnly}
					value={dataStoreId}
				/>
			)}
			{dataStoreDialogStore && (
				<DataStoreDialog
					open
					store={dataStoreDialogStore}
				/>
			)}
			{hasValidDataStoreId && (
				<AggregateStoreDataStoreInfo group={group} />
			)}
		</>
	);
});

export default AggregateStoreGroup;